export {
  DynamicExperience,
  type DynamicExperienceProps,
  type ObservedFormStateChange,
} from './DynamicExperience';

export {
  type Environment,
  type LocalEnvOptions,
  LOCAL_SERVICE_SLUGS,
  buildLocalApiConfig,
  buildLocalApiUrl,
  getApiConfigForEnv,
} from './config/environments';

export {
  ALLOWED_DATA_SOURCES,
  EDX_CANONICAL_TENANT_ID,
  isEndpointAllowed,
  isOriginTrusted,
  isTenantAllowed,
  resolveDataSourceUrl,
  DataSourceNotAllowedError,
  type DataSourceDefinition,
  type AllowedHttpMethod,
  type DataSourceService,
  type TenantScope,
} from './config/allowed-data-sources';

// Interpolation utilities (DB-1.2)
export {
  parse,
  getByPath,
  interpolate,
  interpolateObject,
  hasTemplate,
  hasTemplateDeep,
  extractPaths,
  InterpolationError,
  deriveAnchors,
  buildContextNamespace,
  type InterpolateOptions,
  type OrderedSequenceContext,
  type OrderedSequenceRegistry,
  type DerivedAnchors,
  type Expression,
  type InterpolationContext,
  type Literal,
  type PathSegment,
  type Template,
} from './utils/interpolation';

export {
  registerSequence,
  getSequence,
  academicTermSequence,
  decrementTermYear,
  normalizeTermInput,
  buildThreePointAnchors,
} from './sequences';
export type {
  AnchorMap,
  AcademicTermMember,
  OrderedSequence,
} from './sequences';

// DataSourceManager (DB-1.3)
export {
  DataSourceManagerProvider,
  useDataSourceManager,
  DataSourceCycleError,
  DataSourceMissingDependencyError,
  DataSourcePreconditionRefusedError,
  DataSourceEndpointNotAllowedError,
  DataSourceExtractionError,
  DataSourceHttpError,
  DataSourceTransportError,
  DataSourceUnknownKindError,
  DataSourceReportingNotConfiguredError,
  DataSourceInferenceNotConfiguredError,
  type DataSourceStatus,
  type ResolvedDataSource,
  type DataSourceDeclaration,
  type DataSourceKindMap,
  type DataSourceDeclarationBase,
  type HttpDataSourceDeclaration,
  type ReportingModelDataSourceDeclaration,
  type InferenceDataSourceDeclaration,
  type DataSourceTrigger,
  type DataSourceKind,
  type DataSourceErrorStrategy,
  type DataSourceFallbackContent,
  type DataSourceManagerContextValue,
  type DataSourceManagerProps,
  type FormState,
  type FormStateValue,
} from './providers';

// Data sources the page host resolved on the server, handed to the browser.
export {
  ServerResolvedSourcesProvider,
  type ServerResolvedSources,
} from './providers/ServerResolvedSources';

// What a data-bound component renders while it has no data to render.
export {
  BoundStatusShell,
  resolveBoundStatus,
  type BoundEmptyReason,
  type BoundEmptyStatus,
  type BoundStatus,
  type BoundStatusInput,
  type BoundStatusShellProps,
  type BoundStatusSlot,
  type BoundStatusSlots,
  type BoundStatusSurface,
} from './components/BoundStatusShell';

export {
  DEFAULT_INFERENCE_TIMEOUT_MS,
  DEFAULT_INFERENCE_RETRIES,
} from './utils/data-sources/fetchInferenceDataSource';

export {
  dataSourceTypes,
  resolveDataSourceType,
  createDataSourceTypeRegistry,
  type DataSourceTypeDescriptor,
  type DataSourceTypeRegistry,
  type DataSourceHostConfig,
} from './utils/data-sources/source-types';

// Binding enhancer (DB-1.4)
export {
  applyBindings,
  createBoundRender,
  getBindingMeta,
  BIND_PREFIX,
  ACTION_PREFIX,
  META_PREFIX,
  AUTO_SELECT_FIRST_OPTION_PROP,
  FORM_KEY_PROP,
  isBindDeclaration,
  type BindDeclaration,
  type BindTransform,
  type ActionDeclaration,
  type ActionParamValue,
  type ComponentBindingMeta,
} from './utils/binding-enhancer';

// The compound-control half of `_bind.options`. Not exported from
// `./ce-runtime`: it renders hen-ui item components, and that entry point is
// held to emitting data rather than building UI.
export {
  synthesizeOptionChildren,
  OPTION_CHILD_COMPONENT_TYPES,
} from './utils/option-children';

export {
  ActionDispatcherContext,
  NOOP_ACTION_DISPATCHER,
  useActionDispatcher,
  type ActionDispatcher,
} from './utils/action-resolver/ActionDispatcher';

export {
  ItemScopeProvider,
  useItemScope,
  type ItemScopeProviderProps,
} from './providers/ItemScopeContext';

// The loader contract `platform.hostSlot` fills through.
export {
  HostSlotLoaderProvider,
  useHostSlotLoader,
  type HostSlotActivity,
  type HostSlotContext,
  type HostSlotFillRequest,
  type HostSlotHandle,
  type HostSlotInputs,
  type HostSlotLoader,
  type HostSlotLoading,
  type HostSlotOutcome,
  type HostSlotReasonCode,
  type HostSlotStatus,
} from './components/HostSlot';

export {
  TrackablePlaceholder,
  type TrackablePlaceholderProps,
  Trackable,
  withTrackable,
  type TrackableProps,
  type TrackingKind,
  type TrackingMeta,
} from './components/Trackable';

// EDUN-10692 — DSL render-path eventing surface (TrackingProvider /
// useTracking / StepCorrelation). Exposed so DSL-component authors and
// downstream consumers (e.g. site-publishing apps) can read inherited
// correlation IDs and per-step context without reaching into provider
// internals.
export {
  TrackingProvider,
  DefaultOnTrackingProvider,
  useTracking,
  type TrackingContextValue,
  type TrackingTriggers,
  type TrackingProviderProps,
  type DefaultOnTrackingProviderProps,
  type TrackingHostContext,
  type TrackingHostPolicy,
} from './providers/TrackingProvider';

export {
  composeHostElementKey,
  type HostElementKeyParts,
} from './utils/composeHostElementKey';

export {
  ResultsPageConfigProvider,
  useResultsPageConfig,
  type ResultsPageConfigContextValue,
  type ResultsPageConfigProviderProps,
} from './providers/ResultsPageConfigProvider';

// Governed lead-capture seam for the DSL render path. Exposed so host
// apps and tests can mount the governed drawer and read the
// `openLeadFlow` entry point.
export {
  LeadFlowProvider,
  useLeadFlow,
  useLeadFlowOptional,
  type LeadFlowContextValue,
  type LeadFlowProviderProps,
} from './providers/LeadFlowProvider';

// Submitted-programs registry for the DSL render path. Exposes
// successful-submission state read-only into the DSL
// (`context.submissions` snapshot + the `useSubmittedForProgram` card
// hook); the duplicate-school rule stays governed in `LeadFlowProvider`.
export {
  SubmittedProgramsProvider,
  useSubmittedPrograms,
  useSubmittedProgramsOptional,
  type SubmittedProgramsSnapshot,
  type SubmittedProgramsContextValue,
  type SubmittedProgramsProviderProps,
  type SubmittedResultRef,
} from './providers/SubmittedProgramsProvider';

export {
  useSubmittedForProgram,
  type SubmittedProps,
} from './components/DegreeCardDetailed';

// Providers
export {
  DynamicExperienceProvider,
  useDynamicExperienceContext,
  type DynamicExperienceVariant,
  type EventContext,
} from './providers/DynamicExperienceProvider';

export {
  DE_CONVERSION_EVENT,
  emitConversion,
  subscribeToConversions,
  type DynamicExperienceConversion,
  type DynamicExperienceConversionEvent,
} from './utils/conversionEvents';

export {
  ExperienceConfigProvider,
  useExperienceConfig,
  useApiConfig,
  useTrackingConfig,
  useSiteConfig,
  useLocationConfig,
} from './providers/ExperienceConfigProvider';

// Reducer and state types
export {
  dynamicExperienceReducer,
  actionCreators,
  type FlowState,
  type StepData,
  type ValidationError,
  type ValidationResult,
  type DynamicExperienceState,
  type DynamicExperienceAction,
} from './providers/dynamicExperienceReducer';

// Component registry
export {
  ComponentRenderer,
  hasComponent,
  getComponentTypeFromStep,
  type ComponentProps,
  type ComponentRendererProps,
} from './components/ComponentRegistry';

// ConfigurableStep bridge (DB-1.5)
export {
  default as ConfigurableStep,
  type ConfigurableStepProps,
} from './components/Step/variants/ConfigurableStep';

// Governed Recommender / Spotlight popup surfaces for the DSL result
// page. Exposed so the dev preview and tests can mount the
// platform-owned popups outside `ConfigurableStep`.
export { default as DslResultsPagePopups } from './components/Step/variants/DslResultsPagePopups';

export { Experience } from './components/Experience/Experience';
export { ExperiencePlacement } from './components/Experience/ExperiencePlacement';
export { ExperienceWrapper } from './components/Experience/ExperienceWrapper';
export { Modal } from './components/Experience/Modal';
export { default as SideRail } from './components/SideRail/SideRail';

export * from './types/types';
export * from './types/enums';
export * from './types/config';
export type { PostLeadCtaDestination, Result } from './types/result';
export type {
  ResultCardItem,
  ResultCardPresentation,
} from './types/result-card-presentation';
export {
  attachResultCardPresentation,
  toResultCardPresentation,
} from './utils/mappers/toResultCardPresentation';
export type {
  ResultsPageProps,
  SubmittedProgram,
} from './components/ResultsPage/types';
export { SubmissionStatus } from './components/ResultsPage/types';
export { default as ResultsPage } from './components/ResultsPage';
export {
  ComparisonProvider,
  useComparison,
  useComparisonOptional,
} from './providers/ComparisonProvider';
export * from './services/api';

export { parseHtmlString, uuid, cn } from './shared/utils';

export * from './shared/icons';

// Eventing abstraction (re-exported for downstream consumers like the DSL
// component eventing surface in EDUN-10692). Keep this barrel narrow — only
// surface symbols that external consumers actually need.
export {
  useDynamicExperienceEventing,
  buildEventContext,
  resolveActionOutcome,
  resolveActionOutcomeForStep,
  resolveActionOutcomeForBranch,
  ACTION_OUTCOME_QUESTION,
  ACTION_OUTCOME_RESULTS,
  DEFAULT_FORM_NAME,
  buildStepCorrelation,
  DEFAULT_STEP_PAGE_CATEGORY,
  type DynamicExperienceEventContext,
  type ActionOutcome,
  type CustomDimension,
  type StepCorrelation,
  type BuildStepCorrelationParams,
} from './events';

export { useInfoBanners } from './hooks/useInfoBanners';
export { filterAndPrioritizeInfoBanners } from './utils/filterInfoBanners';
export {
  interpolateCtaUrl,
  type CtaInterpolationSources,
  type CtaMissingValueStrategy,
  type InterpolateCtaUrlOptions,
  type InterpolatedCtaUrl,
} from './utils/interpolateCtaUrl';

// Data-fetch utilities
export * from './utils/data-fetch';

// Pure, window-free lead builders reused server-side by the same-origin lead
// BFF route handlers in apps/hen-site (EDUN-13058). Named exports per repo
// convention (no default re-exports from packages).
export {
  default as buildLeadObject,
  type BuildLeadObjectInputs,
} from './utils/buildLeadObject';
export {
  buildLeadSubmitV4Request,
  type BuildLeadSubmitV4RequestParams,
} from './utils/data-fetch/buildLeadSubmitV4Request';
export { translateStringFields } from './utils/translatePlaceholderForAttribute';
export { getOptionByCustomField } from './utils/getOptionByCustomField';
export {
  buildContextParams,
  MAX_FORWARD_FIELDS,
  MAX_CONTEXT_PARAM_VALUE_LENGTH,
} from './utils/buildContextParams';
export {
  extractCustomFieldFromQueryParam,
  getOptionValueString,
  type ParsedQueryParam,
} from './utils/queryParamUtils';

export {
  convertThemeToCSS,
  convertThemeV2ToCSS,
  type ThemeConfig,
  type ThemeConfigV2,
} from './utils/convertThemeToCSS';
export {
  resolveThemeFontTokens,
  fontFamilyParityGaps,
  FONT_FAMILY_PARITY_FIXTURE,
  FONT_FAMILY_PARITY_EXPECTED,
  FONT_FAMILY_PARITY_ABSENT,
  FONT_FAMILY_ROLES,
  HEADING_LEVELS,
  ADMIN_DEFAULT_FONT_WEIGHTS,
  type FontFamilyRole,
  type HeadingLevel,
  type ResolveThemeFontTokensInput,
  type ResolvedThemeFontTokens,
  type TypographyStateEntry,
} from './utils/resolveThemeFontTokens';
export {
  emitFontSources,
  fontPreloadCandidates,
  FONT_STYLESHEET_HOSTS,
  type DroppedFontFaceField,
  type DroppedFontSource,
  type EmittedFontSources,
  type FontFaceInput,
  type FontFaceSrcInput,
  type FontPreconnect,
  type FontPreloadCandidate,
  type FontSourcesInput,
} from './utils/emitFontSources';

export {
  fetchTenantConfig,
  fetchTenantConfigV2,
  fetchAllTenants,
  searchFlows,
  type TenantResponse,
  type FetchTenantOptions,
  type FetchTenantV2Options,
  type Tenant,
  type FlowSearchResult,
} from './utils/tenantsApi';
// DCS taxonomy (slug→label) — provider is wired internally by
// DynamicExperience; these are exported for SSR prefetch on routes that
// auto-populate DCS (e.g. /discover).
export {
  fetchTaxonomySeed,
  buildTaxonomyMap,
  seedToMap,
  DCS_NAMESPACES,
  type TaxonomyScope,
  type TaxonomySeed,
  type TaxonomySeedEntry,
  type TaxonomyMap,
  type DimensionsResponse,
} from './utils/taxonomy/fetchTaxonomy';
export { TaxonomyProvider, useTaxonomy } from './providers/TaxonomyProvider';

export {
  getCareerDataset,
  type CareerExplorerDataset,
  type CareerPeek,
  type CareerSkill,
  type CareerTrajectoryStep,
  type CareerProgram,
} from './career-explorer';
